import { ChangeDetectionStrategy, Component, ElementRef, Input, OnInit, Renderer2, inject } from '@angular/core';
import { NgClass } from '@angular/common';
import { PorIcon, PorIconDirective } from '@por/shared/ui/icon-lib';

@Component({
    selector: '[apx-button]',
    standalone: true,
    imports: [NgClass, PorIconDirective],
    templateUrl: './button.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApxButtonComponent implements OnInit {
    private readonly el = inject(ElementRef);
    private readonly renderer = inject(Renderer2);

    readonly twClasses = {
        ['base']: ['flex', 'text-button', 'font-roboto', 'font-medium', 'justify-center', 'items-center', 'px-sm', 'py-xs', 'focus:outline-none', 'border', 'border-solid', 'rounded-btn', 'h-btn'],
        ['primary']: ['text-button-primary-text', 'border-button-primary-border', 'bg-button-primary-default', 'focus:bg-button-primary-hover'],
        ['primary-enabled']: ['hover:bg-button-primary-hover'],
        ['secondary']: ['text-button-secondary-text', 'border-button-secondary-border', 'bg-button-secondary-default', 'focus:bg-button-secondary-hover'],
        ['secondary-enabled']: ['hover:bg-button-secondary-hover'],
        ['disabled']: ['opacity-50'],
        ['enabled']: ['cursor-pointer']
    };

    /**
     * Icon variant, for configuring different appearances
     */
    @Input() set variant(variant: 'primary' | 'secondary') {
        if (variant) {
            if (this.variant && this.variant !== variant) {
                this.removeClasses(this.twClasses[this.variant], this.el);
            }
            this.addClasses(this.twClasses[variant], this.el);
            this._variant = variant;
        }
    }

    get variant() {
        return this._variant;
    }

    /**
     * Apex name of the icon.
     * Available icons are contained within the enum.
     */
    @Input() icon!: PorIcon;

    /**
     * Configure the position of the icon with respect to the projected content
     */
    @Input() iconPos: 'before' | 'after' = 'before';
    @Input() iconOnly = false;

    /**
     * Display the loading state
     */
    @Input() set loading(loading: boolean) {
        this._loading = loading;
        // Aria label is only changed if none is provided.
        this.userSetAriaLabel = this.el.nativeElement.getAttribute('aria-label') || null;

        if (this.userSetAriaLabel) {
            return;
        }

        loading ? this.addAriaLabel(this.el, 'Loading') : this.removeAriaLabel(this.el);
    }

    get loading() {
        return this._loading;
    }

    /**
     * Icon variant, for configuring different appearances
     */
    @Input() set disabled(disabled: boolean) {
        const removeClasses = disabled ? [...this.twClasses['enabled'], ...(this.variant ? this.twClasses[`${this.variant}-enabled`] : [])] : this.twClasses['disabled'];

        const addClasses = disabled ? this.twClasses['disabled'] : [...this.twClasses['enabled'], ...(this.variant ? this.twClasses[`${this.variant}-enabled`] : [])];
        this.addClasses(addClasses, this.el);
        this.removeClasses(removeClasses, this.el);
        this._disabled = disabled;
    }
    get disabled() {
        return this._disabled;
    }

    private _variant!: 'primary' | 'secondary';
    private _disabled!: boolean;
    private _loading = false;
    private userSetAriaLabel?: string | null;

    ngOnInit(): void {
        this.addClasses(this.twClasses.base, this.el);
        if (!this.variant) {
            this.variant = 'primary';
        }
        if (this.disabled === undefined) {
            this.disabled = false;
        }
        if (this.iconOnly) {
            this.addIconOnlyStyles();
        }
    }

    private addIconOnlyStyles() {
        const classesToRemove = ['py-xs', 'px-sm', 'border-solid', 'border', 'h-btn', 'hover:bg-button-primary-hover', 'hover:bg-button-secondary-hover', ...this.twClasses[this.variant]];
        this.removeClasses(classesToRemove, this.el);
        this.addClasses(['p-0', 'text-text-base', 'border-0', 'bg-transparent', 'hover:opacity-75', 'focus:opacity-75'], this.el);
    }

    private addClasses(classes: string[], el: ElementRef): void {
        classes.forEach(cls => {
            this.renderer.addClass(el.nativeElement, cls);
        });
    }

    private removeClasses(classes: string[], el: ElementRef): void {
        classes.forEach(cls => {
            this.renderer.removeClass(el.nativeElement, cls);
        });
    }

    private addAriaLabel(el: ElementRef, label: string): void {
        this.renderer.setAttribute(el.nativeElement, 'aria-label', label);
    }

    private removeAriaLabel(el: ElementRef): void {
        this.renderer.removeAttribute(el.nativeElement, 'aria-label');
    }
}
